import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [devUrl, setDevUrl] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            setSent(true);
            if (data.dev_reset_url) setDevUrl(data.dev_reset_url);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="forgot-password-page">
            <div className="panel p-8 sm:p-9">
                <span className="eyebrow text-accent">Account recovery</span>
                <h1 className="font-display text-2xl sm:text-3xl font-bold mt-2.5">
                    Reset your password
                </h1>
                <p className="mt-2.5 text-sm text-dim leading-relaxed">
                    Enter your account email and we'll send you a link to set a new password.
                </p>

                {sent ? (
                    <div className="mt-8" data-testid="forgot-success">
                        <div className="flex items-start gap-3 rounded-xl border border-pos/30 bg-pos/[0.07] p-4">
                            <CheckCircle2 className="text-pos shrink-0 mt-0.5" size={18} />
                            <p className="text-sm text-dim">
                                If an account exists for that email, a password reset link has been sent.
                            </p>
                        </div>
                        {devUrl && (
                            <div className="mt-4 rounded-xl border border-line bg-surface-sunken p-4">
                                <p className="eyebrow text-dim mb-2">Demo mode — no email is sent</p>
                                <Link
                                    to={devUrl.replace(/^https?:\/\/[^/]+/, "")}
                                    data-testid="dev-reset-link"
                                    className="text-sm text-accent link-underline break-all"
                                >
                                    Continue to reset your password →
                                </Link>
                            </div>
                        )}
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-8 space-y-5">
                        <div>
                            <label className="eyebrow text-dim block mb-2">Email</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="forgot-email"
                                className="field"
                            />
                        </div>
                        {error && (
                            <p
                                className="flex items-start gap-2.5 rounded-xl border border-neg/30 bg-neg/[0.07] p-3 text-sm text-neg"
                                data-testid="forgot-error"
                            >
                                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                                {error}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="forgot-submit"
                            className="w-full rounded-full bg-accent text-accent-foreground py-3 text-sm font-semibold hover:bg-foreground transition-colors duration-150 disabled:opacity-50 disabled:hover:bg-accent"
                        >
                            {loading ? "Sending…" : "Send reset link"}
                        </button>
                    </form>
                )}

                <p className="mt-7 pt-6 border-t border-line text-sm text-dim">
                    Remembered it? <Link to="/login" className="text-accent link-underline font-medium">Back to log in</Link>
                </p>
            </div>
        </div>
    );
}
